import { FC, useCallback, useMemo } from 'react'

import { VStack } from '@revolut-internal/ui-kit'

import { useAgentAttendanceIntervalsQuery } from 'modules/Monitoring/api/analytics/queries'
import { ChartTableSkeleton } from 'modules/Monitoring/components/ChartTableSkeleton'
import { LineChart } from 'modules/Monitoring/components/LineChart/LineChart'
import { PageSection } from 'modules/Monitoring/components/PageSection/PageSection'
import { TabBar } from 'modules/Monitoring/components/TabBar/TabBar'
import { GlobalFilterState } from 'modules/Monitoring/GlobalFilters/GlobalFilters'
import { useChannel } from 'modules/Monitoring/hooks/useChannel'
import { usePrepareChartColorMap } from 'modules/Monitoring/hooks/usePrepareChartColorMap'
import { useSelectedCharts } from 'modules/Monitoring/hooks/useSelectedCharts'
import { useTimeRangeParams } from 'modules/Monitoring/hooks/useTimeRangeParams'
import { SpecialisationAttendanceAggregated } from 'modules/Monitoring/types'
import { UrlStateStorageKeys } from 'modules/Monitoring/url-state-storage/consts'
import { useHashedSearchParams } from 'modules/Monitoring/url-state-storage/useHashedSearchParams'
import { prepareChartData } from 'modules/Monitoring/utils/charts/prepareChartData'
import { convertTimeStringToDate } from 'modules/Monitoring/utils/time/convertTimeStringToDate'

import { AgentAttendanceIntervalsTable } from './AgentAttendanceIntervalsTable'
import { AttendanceVariant, AttendanceVariants } from './types'

const ATTENDANCE_INITIAL_STATE = {
  attendanceVariant: AttendanceVariants.Scheduled,
}

export const AgentAttendanceIntervals: FC = () => {
  const { channel, regionedChannel } = useChannel()

  const { state } = useHashedSearchParams<GlobalFilterState>({
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
    contextKey: regionedChannel,
  })
  const {
    state: { attendanceVariant },
    updateState,
  } = useHashedSearchParams<{ attendanceVariant: AttendanceVariant }>({
    hashParamKey: UrlStateStorageKeys.AgentIntervalAttendanceControls,
    initialState: ATTENDANCE_INITIAL_STATE,
  })
  const { from, to, start, end } = useTimeRangeParams({
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
    contextKey: regionedChannel,
  })
  const { selectedCharts, handleChartsSelect } = useSelectedCharts(
    UrlStateStorageKeys.AgentIntervalAttendanceTableSelectedCharts,
  )

  const queueId = state.queues
  const agentId = state.agents
  const specialisationId = state.specialisations
  const departmentId = state.departments
  const areaId = state.areas

  const { data: agentAttendanceIntervals = [], isPending } =
    useAgentAttendanceIntervalsQuery({
      from,
      to,
      start: convertTimeStringToDate({
        timeStr: start,
        useUtc: true,
        shouldReturnDate: false,
        shouldReturnDefaultTime: false,
      }),
      end: convertTimeStringToDate({
        timeStr: end,
        useUtc: true,
        shouldReturnDate: false,
        shouldReturnDefaultTime: false,
      }),
      departmentId,
      areaId,
      specialisationId,
      queueId,
      agentId,
      channel: channel ? [channel] : [],
    })

  const getDataKey = useCallback(
    (data: SpecialisationAttendanceAggregated) => data.specialisation.name,
    [],
  )

  const { colorMap } = usePrepareChartColorMap(getDataKey, agentAttendanceIntervals)

  const scheduledChart = useMemo(
    () =>
      attendanceVariant === AttendanceVariants.Scheduled
        ? prepareChartData({
            data: agentAttendanceIntervals,
            dataKey: getDataKey,
            dataCountKey: 'scheduled',
            colorMap,
          })
        : undefined,
    [agentAttendanceIntervals, attendanceVariant, getDataKey, colorMap],
  )
  const adherenceChart = useMemo(
    () =>
      attendanceVariant === AttendanceVariants.Adherence
        ? prepareChartData({
            data: agentAttendanceIntervals,
            dataKey: getDataKey,
            dataCountKey: 'adherence',
            colorMap,
          })
        : undefined,
    [agentAttendanceIntervals, attendanceVariant, getDataKey, colorMap],
  )
  const chartData = scheduledChart?.chartData || adherenceChart?.chartData || []
  const chartLines = scheduledChart?.charts || adherenceChart?.charts || []
  const chartTooltipLabel =
    attendanceVariant === AttendanceVariants.Scheduled ? 'agent' : '%'

  const handleAttendanceChange = (newAttendanceVariant: AttendanceVariant | null) => {
    if (!newAttendanceVariant) {
      return
    }

    updateState({
      attendanceVariant: newAttendanceVariant,
    })
  }

  return (
    <PageSection
      title="Interval attendance"
      side={
        <TabBar
          behaviourVariant="action"
          variant="segmented fit"
          width={300}
          tabs={[
            { value: AttendanceVariants.Scheduled, label: 'Scheduled' },
            { value: AttendanceVariants.Adherence, label: 'Adherence' },
          ]}
          value={attendanceVariant}
          onChange={handleAttendanceChange}
        />
      }
    >
      {isPending ? (
        <ChartTableSkeleton />
      ) : (
        <VStack space="s-32">
          <LineChart
            data={chartData}
            charts={chartLines}
            selectedCharts={selectedCharts}
            primaryLabel={chartTooltipLabel}
            entityName="Specialisation"
          />

          <AgentAttendanceIntervalsTable
            data={chartData}
            charts={chartLines}
            attendanceVariant={attendanceVariant}
            selectedCharts={selectedCharts}
            onSelectCharts={handleChartsSelect}
          />
        </VStack>
      )}
    </PageSection>
  )
}
